import React, { FC } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Col, Icon, message, Row, Typography } from 'antd'

import { resellerService } from '../../services'
import { Reseller, TabConfig, ResellerFormValues, RequestErrorObject, RequestErrorInterface } from '../../types'
import { Page, AccountActions, RoutedTabs, handleError, ServiceProviderCard, withEntityRequest } from '../../components'
import { useAuth } from '../../providers/AuthProvider'
import RequestError from '../../services/RequestError'

import ChildAccounts from './ChildAccounts'
import Account from './Account'
import Report from './Report'
import LicenseLimits from './LicenseLimits'

const { Title, Text } = Typography

interface RouteParameters {
	id: string
	tab: string
}

interface ResellerProps {
	entity: Reseller
	entityUpdated(newEntity: Reseller): void
}

const ResellerPage: FC<ResellerProps> = ({ entity, entityUpdated }: ResellerProps) => {
	const history = useHistory()
	const { userHasPermission } = useAuth()
	const isMondagoOrServiceProviderAdmin = userHasPermission(['Reseller.Self.ReadWrite', 'Reseller.All.ReadWrite'])
	const isMondagoAdmin = userHasPermission(['ServiceProvider.All.ReadWrite'])
	const leftColumnSpans = {
		xxl: isMondagoOrServiceProviderAdmin ? 18 : 24,
		xl: isMondagoOrServiceProviderAdmin ? 16 : 24,
		lg: isMondagoOrServiceProviderAdmin ? 14 : 24,
		md: 24
	}

	const tabs: TabConfig[] = [
		{
			id: 'children',
			title: (
				<>
					<Icon type='apartment' /> End Customers
				</>
			),
			render: (): React.ReactNode => <ChildAccounts reseller={entity} />
		},
		{
			id: 'details',
			title: (
				<>
					<Icon type='setting' /> Details
				</>
			),
			render: (): React.ReactNode => <Account editable={isMondagoOrServiceProviderAdmin} reseller={entity} submit={editReseller} />
		},
		{
			id: 'report',
			title: (
				<>
					<Icon type='area-chart' /> Realtime Report
				</>
			),
			render: (): React.ReactNode => <Report resellerId={entity.id} resellerName={entity.name} />
		},
		{
			id: 'licenses',
			title: (
				<>
					<Icon type='pound' /> License Limits
				</>
			),
			render: (): React.ReactNode => <LicenseLimits id={entity.id} isMondagoAdmin={isMondagoAdmin} />
		}
	]

	const createResellerDeleteError = (error: RequestError): RequestErrorObject => {
		const source = 'Reseller Delete'
		const base = { status: error.response.status, source }

		switch (error.response.status) {
			case 500:
				return { ...base, description: 'Error in deleting reseller, experienced server error.' }
			case 409:
				return { ...base, description: 'Error in deleting reseller, have you deleted its children?' }
			default:
				return { ...base, description: 'Error deleting reseller' }
		}
	}

	const deleteReseller = async (): Promise<void> => {
		try {
			await resellerService.delete(entity.id)
			history.replace('/reseller')
		} catch (error) {
			handleError(error, createResellerDeleteError)
		}
	}

	const editReseller = async (values: ResellerFormValues): Promise<void> => {
		const createEditResellerError = (error: RequestErrorInterface): RequestErrorObject => {
			const source = 'Reseller Update'
			const base = { status: error.response.status, source }

			switch (error.response.status) {
				case 500:
					return { ...base, description: `Error updating ${entity.name}, experienced a server error.` }
				case 422:
					return { ...base, description: `Error updating ${entity.name}, some information sent is not correct.` }
				default:
					return { ...base, description: `Error updating ${entity.name}` }
			}
		}

		try {
			const { data } = await resellerService.edit(entity.id, values)
			message.success('Reseller has been succesfully updated')
			entityUpdated(data)
		} catch (error) {
			handleError(error, createEditResellerError)
		}
	}

	return (
		<Page
			title={
				<Title>
					<Row type='flex' justify='space-between'>
						<Col>
							{entity.name} <Text type='secondary'>Reseller</Text>
						</Col>
						{isMondagoOrServiceProviderAdmin ? <AccountActions deleteAction={deleteReseller} /> : null}
					</Row>
				</Title>
			}
			subtitle='Reseller'
		>
			<Row gutter={24}>
				<Col {...leftColumnSpans}>
					<RoutedTabs tabs={tabs} />
				</Col>
				{isMondagoOrServiceProviderAdmin ? (
					<Col xxl={6} xl={8} lg={10} md={24}>
						<Row>
							<ServiceProviderCard id={entity.service_provider_id} />
						</Row>
					</Col>
				) : null}
			</Row>
		</Page>
	)
}

const PageWithRequest = withEntityRequest(ResellerPage, { getById: id => resellerService.getById(id), type: 'Reseller' })

const EndCustomerPageWithRequest = (): JSX.Element => {
	const { id } = useParams<RouteParameters>()
	return <PageWithRequest id={parseInt(id, 10)} />
}

export default EndCustomerPageWithRequest
